export type SearchItemKind =
  | "page"
  | "action"
  | "site"
  | "site-action"
  | "worker"
  | "form"
  | "template"
  | "recent-sign-in"
  | "admin";

export type SearchFieldKind =
  | "title"
  | "subtitle"
  | "keyword"
  | "group"
  | "code"
  | "email"
  | "action";

export type SearchRankItem = {
  id: string;
  group: string;
  title: string;
  subtitle?: string;
  keywords?: string[];
  kind?: SearchItemKind;
  searchFields?: SearchRankField[];
};

export type SearchRankField = {
  value?: string | null;
  kind: SearchFieldKind;
};

export type RankedSearchItem<T extends SearchRankItem> = T & {
  searchScore: number;
};

const FIELD_WEIGHTS: Record<SearchFieldKind, number> = {
  title: 1000,
  code: 850,
  email: 720,
  action: 620,
  subtitle: 420,
  keyword: 260,
  group: 90,
};

const GROUP_PRIORITY: Record<string, number> = {
  Workers: 0,
  Sites: 1,
  Forms: 2,
  Templates: 3,
  "Recent sign-ins": 4,
  "Site actions": 5,
  Pages: 6,
  Actions: 7,
  Admin: 8,
};

const ACTION_TERMS = new Set([
  "activity",
  "attendance",
  "entrance",
  "entrances",
  "gate",
  "gates",
  "qr",
  "sign",
  "signin",
  "signins",
  "sign-in",
  "sign-ins",
]);

export function rankSearchItems<T extends SearchRankItem>(
  items: T[],
  query: string
): RankedSearchItem<T>[] {
  const tokens = tokenize(query);

  if (!tokens.length) {
    return items.map((item) => ({ ...item, searchScore: 0 }));
  }

  const queryHasActionIntent = tokens.some((token) => ACTION_TERMS.has(token));

  return items
    .map((item, index) => {
      const score = scoreItem(item, tokens, queryHasActionIntent);
      return { item, index, score };
    })
    .filter((result) => result.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;

      const groupDelta =
        (GROUP_PRIORITY[left.item.group] ?? 99) -
        (GROUP_PRIORITY[right.item.group] ?? 99);
      if (groupDelta !== 0) return groupDelta;

      const titleDelta = left.item.title.localeCompare(right.item.title);
      if (titleDelta !== 0) return titleDelta;

      return left.index - right.index;
    })
    .map(({ item, score }) => ({ ...item, searchScore: score }));
}

export function tokenize(value: string) {
  return normalize(value)
    .split(" ")
    .filter((token) => token.length > 0);
}

function scoreItem(
  item: SearchRankItem,
  tokens: string[],
  queryHasActionIntent: boolean
) {
  const fields = getFields(item);
  let total = 0;

  for (const token of tokens) {
    let tokenScore = 0;
    for (const field of fields) {
      tokenScore = Math.max(tokenScore, scoreField(field, token));
    }
    if (tokenScore <= 0) return 0;
    total += tokenScore;
  }

  total += entityBoost(item, tokens, queryHasActionIntent);
  return total;
}

function getFields(item: SearchRankItem): SearchRankField[] {
  return [
    { value: item.title, kind: "title" as const },
    { value: item.subtitle, kind: "subtitle" as const },
    ...(item.keywords ?? []).map((value) => ({
      value,
      kind: "keyword" as const,
    })),
    { value: item.group, kind: "group" as const },
    ...(item.searchFields ?? []),
  ].filter((field) => Boolean(field.value));
}

function scoreField(field: SearchRankField, token: string) {
  const normalized = normalize(field.value ?? "");
  if (!normalized) return 0;

  const compact = normalized.replace(/\s/g, "");
  const compactToken = token.replace(/\s/g, "");
  const weight = FIELD_WEIGHTS[field.kind];

  if (normalized === token || compact === compactToken) return weight + 420;
  if (normalized.startsWith(token) || compact.startsWith(compactToken)) {
    return weight + 300;
  }
  if (normalized.split(" ").some((word) => word.startsWith(token))) {
    return weight + 220;
  }
  if (normalized.includes(token) || compact.includes(compactToken)) {
    return weight + 80;
  }

  return 0;
}

function entityBoost(
  item: SearchRankItem,
  tokens: string[],
  queryHasActionIntent: boolean
) {
  const title = normalize(item.title);
  const subtitle = normalize(item.subtitle ?? "");
  const titleMatchesAll = tokens.every((token) => title.includes(token));
  const subtitleMatchesAll = tokens.every((token) => subtitle.includes(token));

  if (item.kind === "worker" && (titleMatchesAll || subtitleMatchesAll)) {
    return 700;
  }

  if (item.kind === "site" && titleMatchesAll) {
    return 420;
  }

  if (item.kind === "site-action") {
    return queryHasActionIntent ? 520 : -380;
  }

  if (item.kind === "recent-sign-in") {
    return -160;
  }

  return 0;
}

function normalize(value: string) {
  return value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}
